// @flow
import { Collapse } from 'antd'
import FormikToggleField from 'components/formik/FormikToggleField'
import { Formik } from 'formik'
import { contextNames } from 'pages/Utils/extra-contexts'
import * as React from 'react'
import { ZentakeContext } from 'utils/ZentakeContext'

import SettingsMessages from '@/pages/Settings/SettingsTab/SettingsMessages'
import { SettingsReminder } from '@/pages/Settings/SettingsTab/SettingsReminder'
import { useLoadReminderById } from '@/queries/reminders/useLoadReminderById'
import { useLoadGlobalReminder } from '@/queries/reminders/useLoadReminders'
import { useLoadResourcesSettings } from '@/queries/resources_settings/useLoadResourcesSettings'
import Tooltip from '@/ui/atoms/Tooltip'

import FormChangesListener from './FormChangesListener'

type ExtraContext = { [string]: any }

type Props = {
  resourceId: string | number,
  extraContexts: ExtraContext,
  setExtraContexts: Function,
  resourceType: string,
}

function FormControlsSettingsModalForm({
  extraContexts,
  resourceId,
  setExtraContexts,
  resourceType,
}: Props): React.Node {
  const { data: resourceSettingsData } = useLoadResourcesSettings(
    String(resourceId)
  )

  const reminderByResourceTypeId = { [resourceType]: String(resourceId) }

  const { data: reminderData } = useLoadReminderById({
    ...reminderByResourceTypeId,
  })
  const { data: globalReminderData } = useLoadGlobalReminder()

  const { teamConfig } = React.useContext(ZentakeContext)

  const contextName = contextNames.customNotifications

  const initialValues = React.useMemo(() => {
    return {
      resourceId,
      initialValueKey: Math.round(Math.random() * 21),
      useCustomInvite: resourceSettingsData?.use_custom_messaging || false,
      emailSubject:
        resourceSettingsData?.email_subject ||
        teamConfig?.send_email_subject ||
        '',
      emailMessage:
        resourceSettingsData?.email_content ||
        teamConfig?.send_email_content ||
        '',
      textMessage:
        resourceSettingsData?.sms_content || teamConfig?.sms_content || '',
      useReminderInvite: reminderData?.[0]?.enabled,
      email_reminder_subject:
        reminderData?.[0]?.email_subject || globalReminderData?.email_subject,
      email_body:
        reminderData?.[0]?.email_body ||
        resourceSettingsData?.email_body ||
        globalReminderData?.email_body,
      sms_body: reminderData?.[0]?.sms_body || globalReminderData?.sms_body,
      reminder_days: reminderData?.[0]?.reminder_days || '0',
    }
  }, [
    resourceId,
    resourceSettingsData,
    globalReminderData,
    reminderData,
    teamConfig,
  ])

  const onValuesChange = React.useCallback(
    (values) => {
      const nextExtraContexts = {
        ...extraContexts,
        [contextName]: {
          ...extraContexts[contextName],
          values: {
            [resourceType]: String(resourceId),
            id_reminder: reminderData?.[0]?.id,
            enabled: reminderData?.[0]?.enabled,
            reminder_condition:
              reminderData?.[0]?.reminder_condition ||
              globalReminderData?.reminder_condition,
            reminder_days:
              reminderData?.[0]?.reminder_days ||
              globalReminderData?.reminder_days,
            ...values,
          },
        },
      }
      setExtraContexts(nextExtraContexts)
    },
    [
      resourceId,
      resourceType,
      contextName,
      extraContexts,
      setExtraContexts,
      reminderData,
      globalReminderData,
    ]
  )

  React.useEffect(() => {
    const contextValues = extraContexts[contextName]?.values

    if (!contextValues || !Object.keys(contextValues).length) {
      if (!resourceSettingsData || !Object.keys(resourceSettingsData).length) {
        return
      }
      if (!reminderData || !Object.keys(reminderData).length) {
        return
      }

      const nextExtraContexts = {
        ...extraContexts,
        [contextName]: {
          ...extraContexts[contextName],
          values: {
            ...(extraContexts[contextName] || {}),
            id: resourceId,
            useCustomInvite: resourceSettingsData.use_custom_messaging,
            emailSubject: resourceSettingsData.email_subject,
            emailMessage: resourceSettingsData.email_content,
            textMessage: resourceSettingsData.sms_content,
            useReminderInvite: Boolean(reminderData?.[0].enabled),
            [resourceType]: String(resourceId),
            id_reminder: reminderData?.[0]?.id,
            enabled: reminderData?.[0]?.enabled,
            reminder_condition:
              reminderData?.[0]?.reminder_condition ||
              resourceSettingsData?.reminder_condition ||
              globalReminderData?.reminder_condition,
            reminder_days:
              reminderData?.[0]?.reminder_days ||
              resourceSettingsData?.reminder_days ||
              globalReminderData?.reminder_days ||
              0,
            email_reminder_subject:
              reminderData?.[0]?.email_subject ||
              resourceSettingsData?.email_subject ||
              globalReminderData?.email_subject,
            email_body:
              reminderData?.[0]?.email_body ||
              resourceSettingsData?.email_body ||
              globalReminderData?.email_body,
            sms_body:
              reminderData?.[0]?.sms_body ||
              resourceSettingsData?.sms_body ||
              globalReminderData?.sms_body,
          },
        },
      }

      setExtraContexts(nextExtraContexts)
    }
  }, [
    resourceType,
    resourceId,
    contextName,
    extraContexts,
    resourceSettingsData,
    globalReminderData,
    reminderData,
    setExtraContexts,
  ])

  let collapseInviteTitle = 'Custom Form Invite'
  let collapseInviteDescription = 'Send custom message for this Form'

  let collapseReminderTitle = 'Custom Form Reminder Invite'
  let collapseReminderDescription = 'Send custom reminder for this Form'

  if (resourceType === 'document') {
    collapseInviteTitle = 'Custom Document Invite'
    collapseInviteDescription = 'Send custom message for this Document'

    collapseReminderTitle = 'Custom Document Reminder Invite'
    collapseReminderDescription = 'Send custom reminder for this Document'
  }

  if (resourceType === 'packet') {
    collapseInviteTitle = 'Custom Packet Invite'
    collapseInviteDescription = 'Send custom message for this Packet'

    collapseReminderTitle = 'Custom Packet Reminder Invite'
    collapseReminderDescription = 'Send custom reminder for this Packet'
  }

  const inviteTooltipTitle = {
    false: 'Click to active and customize the invite message',
    true: 'Click to disable the invite message',
  }

  const reminderTooltipTitle = {
    false: 'Click to active and customize the reminder message',
    true: 'Click to disable the reminder message',
  }

  return (
    <Formik initialValues={initialValues} onSubmit={() => undefined}>
      {({ values }) => {
        return (
          <div className="flex flex-col gap-6">
            <Collapse
              activeKey={values.useCustomInvite ? ['1'] : []}
              className="bg-white [&_.ant-collapse-header:hover]:!bg-transparent [&_.ant-collapse-header]:!py-5"
              collapsible={values.useCustomInvite ? 'header' : 'disabled'}
              items={[
                {
                  key: '1',
                  label: (
                    <div className="grid grid-cols-[1fr_50px] items-center gap-4">
                      <div className="flex w-full flex-col">
                        <h2 className="-mt-0.5 text-lg font-medium text-gray-800">
                          {collapseInviteTitle}
                        </h2>
                        <p className="text-sm text-gray-500">
                          {collapseInviteDescription}
                        </p>
                      </div>
                    </div>
                  ),
                  extra: (
                    <div className="mt-2 flex h-full items-center justify-center">
                      <Tooltip
                        mouseEnterDelay={0.4}
                        title={
                          inviteTooltipTitle[String(values.useCustomInvite)]
                        }
                      >
                        <i>
                          <FormikToggleField
                            compact
                            horizontal
                            lightLabel
                            primary
                            name="useCustomInvite"
                          />
                        </i>
                      </Tooltip>
                    </div>
                  ),
                  children: (
                    <div className="px-2 py-3">
                      <SettingsMessages />
                    </div>
                  ),
                },
              ]}
            />

            <Collapse
              activeKey={values.useReminderInvite ? ['1'] : []}
              className="bg-white [&_.ant-collapse-header:hover]:!bg-transparent [&_.ant-collapse-header]:!py-5"
              collapsible={values.useReminderInvite ? 'header' : 'disabled'}
              items={[
                {
                  key: '1',
                  label: (
                    <div className="grid grid-cols-[1fr_50px] items-center gap-4">
                      <div className="flex w-full flex-col">
                        <h2 className="-mt-0.5 text-lg font-medium text-gray-800">
                          {collapseReminderTitle}
                        </h2>
                        <p className="text-sm text-gray-500">
                          {collapseReminderDescription}
                        </p>
                      </div>
                    </div>
                  ),
                  extra: (
                    <div className="mt-2 flex h-full items-center justify-center">
                      <Tooltip
                        mouseEnterDelay={0.4}
                        title={
                          reminderTooltipTitle[String(values.useReminderInvite)]
                        }
                      >
                        <i>
                          <FormikToggleField
                            compact
                            horizontal
                            lightLabel
                            primary
                            name="useReminderInvite"
                          />
                        </i>
                      </Tooltip>
                    </div>
                  ),
                  children: (
                    <div className="relative px-2 py-3">
                      <SettingsReminder />
                    </div>
                  ),
                },
              ]}
            />
            <FormChangesListener onChange={onValuesChange} />
          </div>
        )
      }}
    </Formik>
  )
}

export default FormControlsSettingsModalForm
